import { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import { useSavedCallback } from '../hooks/useSavedCallback.js';
import { flatpickrFixEventsPlugin } from './plugins/fixEvents.js';
import { updateClassNames } from './utils.js';
export const useDatePicker = ({ value, onChange, dateFormat = 'Y-m-d', inline, disable, enable, maxDate, minDate, readOnly = false, prefix, onOpen, onClose, allowInput, closeOnSelect = true, noCalendar = false, static: maybeStatic = false, mode, showMonths, enableTime = false, }) => {
    const startInputField = useRef(null);
    const endInputField = useRef(null);
    const calendarRef = useRef(null);
    const savedOnChange = useSavedCallback(onChange);
    const savedOnClose = useSavedCallback(onClose);
    const savedOnOpen = useSavedCallback(onOpen);
    useEffect(() => {
        if (startInputField.current === null) {
            return;
        }
        const onHook = (_selectedDates, _dateStr, instance, prefix) => {
            updateClassNames(instance, prefix);
            if (startInputField === null || startInputField === void 0 ? void 0 : startInputField.current) {
                startInputField.current.readOnly = readOnly;
            }
            if (endInputField === null || endInputField === void 0 ? void 0 : endInputField.current) {
                endInputField.current.readOnly = readOnly;
            }
        };
        const enableOrDisable = enable ? 'enable' : 'disable';
        let enableOrDisableArr;
        if (!enable && !disable) {
            enableOrDisableArr = [];
        }
        else if (enable) {
            enableOrDisableArr = enable;
        }
        else {
            enableOrDisableArr = disable;
        }
        const { current: start } = startInputField;
        const { current: end } = endInputField;
        const calendar = flatpickr(start, {
            inline: inline !== null && inline !== void 0 ? inline : false,
            disableMobile: true,
            defaultDate: value,
            closeOnSelect,
            mode,
            allowInput: allowInput !== null && allowInput !== void 0 ? allowInput : true,
            dateFormat,
            [enableOrDisable]: enableOrDisableArr,
            minDate,
            maxDate,
            plugins: [
                flatpickrFixEventsPlugin({
                    inputFrom: startInputField.current,
                    inputTo: endInputField.current,
                }),
            ],
            clickOpens: !readOnly,
            noCalendar,
            static: maybeStatic,
            showMonths,
            enableTime,
            time_24hr: true,
            onChange: (...args) => {
                if (savedOnChange && !readOnly) {
                    savedOnChange(...args);
                }
            },
            onClose: savedOnClose,
            onReady: onHook,
            onMonthChange: onHook,
            onYearChange: onHook,
            onOpen: (...args) => {
                onHook(...args);
                savedOnOpen(...args);
            },
            onValueUpdate: onHook,
        });
        calendarRef.current = calendar;
        function handleArrowDown(event) {
            if (event.code === 'Escape') {
                calendar.calendarContainer.classList.remove('open');
            }
            if (event.code === 'ArrowDown') {
                const { calendarContainer, selectedDateElem: fpSelectedDateElem, todayDateElem: fptodayDateElem, } = calendar;
                const selectedDateElem = calendarContainer.querySelector('.selected') && fpSelectedDateElem;
                const todayDateElem = calendarContainer.querySelector('.today') && fptodayDateElem;
                (selectedDateElem ||
                    todayDateElem ||
                    calendarContainer.querySelector('.flatpickr-day[tabindex]') ||
                    calendarContainer).focus();
            }
        }
        function handleOnChange() {
            if (start.value !== '') {
                return;
            }
            if (!calendar.selectedDates) {
                return;
            }
            if (calendar.selectedDates.length === 0) {
                return;
            }
            calendar.clear();
            calendar.input.focus();
        }
        if (start) {
            start.addEventListener('keydown', handleArrowDown);
            start.addEventListener('change', handleOnChange);
            if (calendar && calendar.calendarContainer) {
                calendar.calendarContainer.setAttribute('role', 'application');
                calendar.calendarContainer.setAttribute('aria-label', 'calendar-container');
            }
        }
        if (end) {
            end.addEventListener('keydown', handleArrowDown);
            end.addEventListener('change', handleOnChange);
        }
        return () => {
            if (calendar && calendar.destroy) {
                calendar.destroy();
            }
            if (start) {
                start.removeEventListener('keydown', handleArrowDown);
                start.removeEventListener('change', handleOnChange);
            }
            if (end) {
                end.removeEventListener('keydown', handleArrowDown);
                end.removeEventListener('change', handleOnChange);
            }
        };
    }, [savedOnChange, savedOnClose, savedOnOpen, readOnly]);
    useEffect(() => {
        var _a;
        if ((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) {
            calendarRef.current.set({ dateFormat });
        }
    }, [dateFormat]);
    useEffect(() => {
        var _a;
        if ((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) {
            calendarRef.current.set('minDate', minDate);
        }
    }, [minDate]);
    useEffect(() => {
        var _a;
        if ((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) {
            calendarRef.current.set('maxDate', maxDate);
        }
    }, [maxDate]);
    useEffect(() => {
        var _a;
        if (((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) && disable) {
            calendarRef.current.set('disable', disable);
        }
    }, [disable]);
    useEffect(() => {
        var _a;
        if (((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) && enable) {
            calendarRef.current.set('enable', enable);
        }
    }, [enable]);
    useEffect(() => {
        var _a;
        if (((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) && inline) {
            calendarRef.current.set('inline', inline);
        }
    }, [inline]);
    useEffect(() => {
        var _a;
        if ((_a = calendarRef === null || calendarRef === void 0 ? void 0 : calendarRef.current) === null || _a === void 0 ? void 0 : _a.set) {
            if (value !== undefined) {
                calendarRef.current.setDate(value);
            }
            updateClassNames(calendarRef.current, prefix);
        }
        else if (!calendarRef.current && value) {
            startInputField.current.value = value;
        }
    }, [value, prefix]);
    return {
        startInputField,
        endInputField,
        calendarRef,
    };
};
//# sourceMappingURL=useDatePicker.js.map